import { uz } from './locales/uz';
import { ru } from './locales/ru';
import { kk } from './locales/kk';
import { ruKz } from './locales/ru.kz';
import { ruKg } from './locales/ru.kg';
import { countryConfig } from '../country';

// 'kk' (Kazakh) is a KZ-only locale — KZ ships `locales: ['ru', 'kk']`, UZ ships
// ['uz', 'ru']. All three catalogs are bundled, but LOCALES (from the active
// country) decides which the deployment actually routes/renders.
export type Locale = 'uz' | 'ru' | 'kk';
export const LOCALES = [...countryConfig.locales] as Locale[];
// The audience is Russian-dominant, so ru is the default UI language (guests
// with no stored/browser preference) and the fallback catalog.
export const DEFAULT_LOCALE = countryConfig.defaultLocale as Locale;
// The locales search engines are told about (hreflang, x-default, sitemap), in
// LOCALES order. Every locale on UZ; ru alone on KZ since 2026-10-18, where
// /kk still works for people but is noindex and prerendered no more.
const indexed: readonly string[] = countryConfig.indexedLocales ?? countryConfig.locales;
export const INDEXED_LOCALES = LOCALES.filter((l) => indexed.includes(l));

type Catalog = Record<string, string>;

// `ru` serves both deployments. On Kazakhstan the sentences that are only true
// in Uzbekistan (payment providers, «на узбекском», Uzbek portals, Telegram
// alerts) are replaced from `locales/ru.kz.ts`; on Uzbekistan the catalog is
// `ru` itself, the same object as before. See that file for the rules.
// Kyrgyzstan has its own layer (`locales/ru.kg.ts`), laid over `ru` alone — never
// over the Kazakh one, so no Kazakhstan sentence can leak onto manastender.com.
const ruForCountry: Catalog =
  countryConfig.code === 'kz'
    ? { ...ru, ...ruKz }
    : countryConfig.code === 'kg'
      ? { ...ru, ...ruKg }
      : ru;

const catalogs: Record<Locale, Catalog> = { uz, ru: ruForCountry, kk };

// Brand identity is interpolated into copy via {{brand}} / {{brandEmail}} /
// {{brandHost}} tokens so the catalogs hold NO hardcoded UzTender/uztender.com
// string — the KZ build renders KazTender.com / kaztender.com from the same
// source strings. These are ALWAYS merged into the interpolation params (even
// when a caller passes none), so a bare `translate(locale, key)` still resolves
// the brand tokens. UZ values equal the former literals, so UZ copy is unchanged.
const BRAND_TOKENS: Record<string, string> = {
  brand: countryConfig.brand.name,
  brandEmail: countryConfig.brand.supportEmail,
  brandHost: new URL(countryConfig.brand.siteUrl).host,
  // Company-id term + digit count, so copy that names it renders the country's
  // own scheme: UZ «ИНН» / 9, KZ «БИН/ИИН» / 12. UZ values equal the former
  // literals, so UZ copy is unchanged.
  tin: countryConfig.companyIdForm.label,
  tinDigits: String(countryConfig.companyIdForm.digits),
};

// The company-id term in the LOCALE's own language where it differs from the
// config's (Russian) label: Kazakh copy says «БСН/ЖСН», and every `{{tin}}`
// phrase in kk.ts rendered «БИН/ИИН» until 2026-10-19 (QA on the onboarding).
const TIN_BY_LOCALE: Partial<Record<Locale, string>> =
  countryConfig.code === 'kz' ? { kk: 'БСН/ЖСН' } : {};

export function translate(
  locale: Locale,
  key: string,
  params?: Record<string, string | number>,
): string {
  const catalog = catalogs[locale];
  const fallbackCatalog = catalogs[DEFAULT_LOCALE];
  let value = catalog[key] ?? fallbackCatalog[key] ?? key;
  // {{currency}} is locale-dependent (ru «сум»/«тенге», uz «so'm»), so it is
  // computed here rather than in the static BRAND_TOKENS.
  const currency =
    countryConfig.currency.words[locale] ?? countryConfig.currency.words[DEFAULT_LOCALE] ?? '';
  // Country name in three cases (also locale-dependent) — {{country}} nominative,
  // {{countryGen}} genitive, {{countryPrep}} prepositional.
  const cn = countryConfig.countryName[locale] ?? countryConfig.countryName[DEFAULT_LOCALE];
  const merged = {
    ...BRAND_TOKENS,
    tin: TIN_BY_LOCALE[locale] ?? BRAND_TOKENS['tin'],
    currency,
    country: cn?.nom ?? '',
    countryGen: cn?.gen ?? '',
    countryPrep: cn?.prep ?? '',
    ...(params ?? {}),
  };
  for (const [k, v] of Object.entries(merged)) {
    /*
     * `replaceAll`, not `replace`. With a STRING needle `replace` swaps only
     * the FIRST occurrence, so a phrase naming the same value twice rendered
     * the second one as a literal `{{name}}`.
     *
     * Shipped that way to production on 2026-08-14 in `invoice.note`
     * («Тариф Бизнес активируется… Назначение платежа: оплата подписки
     * {{name}}») — on the printable invoice, in the sentence telling the
     * buyer what to write as the payment purpose. Caught by looking at the
     * rendered page, not by any test: every existing string happened to use
     * each placeholder once, so nothing exercised the second occurrence.
     *
     * A string needle is also why this is safe: no regex escaping, no
     * special meaning for `$&` in the replacement.
     */
    value = value.replaceAll(`{{${k}}}`, String(v));
  }
  return value;
}

/** A locale THIS deployment serves (UZ: uz + ru, KZ: ru + kk). */
function isServedLocale(value: string | null | undefined): value is Locale {
  return value != null && (LOCALES as string[]).includes(value);
}

/**
 * The UI language a path asks for. Only a locale the deployment serves counts:
 * every catalog is bundled into both builds, so before 2026-10-16 any
 * `kaztender.com/uz/…` (linked from the stale `/uz/*` pages the KZ releases
 * carried) rendered the whole interface in Uzbek. An unserved prefix is now
 * just an unknown path in the default language.
 */
export function resolveLocaleFromPath(pathname: string): Locale {
  const segment = pathname.split('/')[1];
  return isServedLocale(segment) ? segment : DEFAULT_LOCALE;
}

export function getLocaleFromStorage(): Locale | null {
  // SSR-safe: check for window
  if (typeof window === 'undefined') return null;
  try {
    // Any served locale — this accepted only `uz`/`ru`, so a reader who chose
    // «Қазақша» on kaztender.com was never remembered.
    const stored = localStorage.getItem('locale');
    if (isServedLocale(stored)) return stored;
  } catch {
    // ignore
  }
  return null;
}

export function setLocaleInStorage(locale: Locale): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem('locale', locale);
  } catch {
    // ignore
  }
}
